import classNames from "classnames";
import type FlickityFull from "flickity-fullscreen";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { safeDestroy } from "../../hooks/useFlickity";
import { focusElement, trapFocus } from "../../utils/keyboardFocus";
import { usePageSetting } from "../../utils/settings";
import { UGCTileWithPost } from "../../utils/ugcTiles";
import { loadWistiaPlayer } from "../../utils/wistia";
import { Image } from "../Image";
import { SVGIcon } from "../SVGIcon";
import { SocialIcon } from "../SocialIcon";
import { UGCRecommendations } from "../UGCRecommendations";

// Not using useFlickity hook: uses flickity-fullscreen with complex event handlers and resize reinit
type FlickityFullConstructor = new (
    el: HTMLElement,
    options: object,
) => FlickityFull;

const MOBILE_WIDTH_THRESHOLD = 501;
const MEDIUM_SCREEN_WIDTH_THRESHOLD = 769;

const isMobileWidth = (): boolean => {
    const window = getWindow();
    return (window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD;
};

const isMediumScreenWidth = (): boolean => {
    const window = getWindow();
    return (window?.innerWidth ?? 0) < MEDIUM_SCREEN_WIDTH_THRESHOLD;
};

const gotoGalleryLink = (
    event: React.MouseEvent<HTMLElement>,
    link?: string,
): void => {
    if (event) {
        event.preventDefault();
    }

    const window = getWindow();
    if (link && window) {
        window.open(link, "_blank");
    }
};

const isTilePhotoOrVideo = (tile: UGCTileWithPost): boolean => {
    return !!(
        (tile.post.post_type === "image" || tile.post.post_type === "video") &&
        tile.post.photo
    );
};

const isTextOnly = (tile: UGCTileWithPost): boolean => {
    return tile.post.post_type === "text-only";
};

const getGalleryArrow = (isMobile: boolean) => {
    return isMobile
        ? {
              x0: 10,
              x1: 60,
              y1: 50,
              x2: 60,
              y2: 40,
              x3: 20,
          }
        : {
              x0: 35,
              x1: 110,
              y1: 120,
              x2: 115,
              y2: 120,
              x3: 40,
          };
};

const hasVideoTile = (tiles: readonly UGCTileWithPost[]): boolean => {
    return tiles.some(
        (tile) =>
            tile.post.post_type === "video" && tile.post.video_link !== "",
    );
};

export const UGCGallery = (
    props: Immutable<{
        /** Pre-filtered tiles (use `filterUGCTilesWithPosts` at Grid/Carousel). */
        tiles: readonly UGCTileWithPost[];
        /**
         * 0-based Flickity cell index into `tiles`
         * (same space as rendered gallery cells / `inert` selection).
         */
        initialSlide: number;
        onCloseGallery: () => void;
    }>,
): React.ReactNode => {
    const brandName = usePageSetting("app_display_name") || "";
    const galleryTiles = props.tiles;
    const initialCellIndex = Math.min(
        Math.max(0, props.initialSlide),
        Math.max(0, galleryTiles.length - 1),
    );
    const [isMobile, setIsMobile] = React.useState(false);
    const [isMediumScreen, setIsMediumScreen] = React.useState(false);
    const [isGalleryFullscreen, setIsGalleryFullscreen] = React.useState(false);
    // 1-based for the "N of M" counter; keep in sync with initial Flickity cell.
    const [selectedSlide, setSelectedSlide] = React.useState(
        initialCellIndex + 1,
    );

    const galleryCarouselRef = React.useRef<HTMLDivElement>(null);
    const galleryFlktyRef = React.useRef<FlickityFull | null>(null);
    const flickityClassRef = React.useRef<FlickityFullConstructor | null>(null);
    const hasInitialVideoTile = React.useRef(hasVideoTile(galleryTiles));

    // Use ref to avoid callback dependency on props.onCloseGallery
    const onCloseGalleryRef = React.useRef(props.onCloseGallery);
    React.useEffect(() => {
        onCloseGalleryRef.current = props.onCloseGallery;
    }, [props.onCloseGallery]);

    // Required: Event listener and effect dependency needs stable reference for proper cleanup
    const handleCloseGallery = React.useCallback(() => {
        if (galleryFlktyRef.current) {
            galleryFlktyRef.current.exitFullscreen();
            safeDestroy(galleryFlktyRef.current);
        }

        onCloseGalleryRef.current();
    }, []);

    // Required: Event listener needs stable reference for proper cleanup
    const initializeGallery = React.useCallback(
        (mobile: boolean) => {
            if (galleryFlktyRef.current) {
                safeDestroy(galleryFlktyRef.current);
            }

            const FlickityClass = flickityClassRef.current;
            const galleryArrow = getGalleryArrow(mobile);

            if (galleryCarouselRef.current && FlickityClass) {
                const flkty = new FlickityClass(galleryCarouselRef.current, {
                    accessibility: true,
                    cellAlign: "center",
                    pageDots: false,
                    fullscreen: true,
                    cellSelector: ".ugc-carousel-gallery-tile",
                    arrowShape: galleryArrow,
                });

                galleryFlktyRef.current = flkty;

                flkty.select(initialCellIndex);
                flkty.on("select", (index) => {
                    setSelectedSlide(Number(index) + 1);
                });

                flkty.on("settle", () => {
                    const document = getDocument();
                    const activeModal = document?.querySelector<HTMLElement>(
                        ".ugc-carousel-gallery__content",
                    );
                    if (activeModal) {
                        trapFocus(activeModal);
                    }

                    const activeCloseButton =
                        document?.querySelector<HTMLButtonElement>(
                            ".ugc-carousel-gallery-tile.is-selected .ugc-carousel-gallery-tile__close",
                        );
                    if (activeCloseButton) {
                        // focus() is fired but it is not applied properly for some reason
                        // This hack makes focus() working properly
                        setTimeout(() => {
                            focusElement(activeCloseButton);
                        }, 0);
                    }
                    if (galleryFlktyRef.current) {
                        galleryFlktyRef.current.viewFullscreen();
                    }
                });

                flkty.on("dragStart.flickity", () => {
                    const document = getDocument();
                    if (document) {
                        document.ontouchmove = (event) => {
                            event.preventDefault();
                        };
                    }
                });

                flkty.on("dragEnd.flickity", () => {
                    const document = getDocument();
                    if (document) {
                        document.ontouchmove = () => {
                            return true;
                        };
                    }
                });

                flkty.on("fullscreenChange", (isFullscreen) => {
                    setIsGalleryFullscreen(Boolean(isFullscreen));
                    if (!isFullscreen && galleryFlktyRef.current) {
                        safeDestroy(galleryFlktyRef.current);
                        onCloseGalleryRef.current();
                        return;
                    }

                    const document = getDocument();
                    const nextBtn = document?.querySelector<HTMLElement>(
                        ".flickity-button.flickity-prev-next-button.next",
                    );
                    const prevBtn = document?.querySelector<HTMLElement>(
                        ".flickity-button.flickity-prev-next-button.previous",
                    );

                    if (nextBtn) {
                        nextBtn.setAttribute(
                            "aria-label",
                            t`View Next. Activating this element will cause content on the page to be updated.`,
                        );
                        nextBtn.classList.add("al-ugc-gallery-flickity--next");
                    }

                    if (prevBtn) {
                        prevBtn.setAttribute(
                            "aria-label",
                            t`View Previous. Activating this element will cause content on the page to be updated.`,
                        );
                        prevBtn.classList.add(
                            "al-ugc-gallery-flickity--previous",
                        );
                    }
                });

                flkty.on("pointerDown", (event) => {
                    if (mobile) {
                        return;
                    }

                    if (event.target) {
                        const target = event.target as HTMLElement;
                        if (
                            !target.classList.contains(
                                "ugc-carousel-gallery-tile",
                            )
                        ) {
                            return;
                        }
                        handleCloseGallery();
                    }
                });
            }
        },
        [handleCloseGallery, initialCellIndex],
    );

    // Handle resize and initialize gallery
    React.useEffect(() => {
        const window = getWindow();
        if (!window) return;

        // Skip on SSR - flickity requires window
        if (import.meta.env.SSR) {
            return;
        }

        let isMounted = true;
        let resizeHandler: (() => void) | null = null;

        const checkMobileState = () => {
            const mobile = isMobileWidth();
            const mediumScreen = isMediumScreenWidth();

            setIsMobile(mobile);
            setIsMediumScreen(mediumScreen);
            initializeGallery(mobile);
        };

        import("flickity-fullscreen")
            .then((module) => {
                if (!isMounted) return;

                flickityClassRef.current = module.default;
                checkMobileState();
                resizeHandler = checkMobileState;
                window.addEventListener("resize", checkMobileState);
            })
            .catch((error) => {
                if (!isMounted) return;
                console.error("Failed to load flickity-fullscreen:", error);
            });

        return () => {
            isMounted = false;
            if (resizeHandler) {
                window.removeEventListener("resize", resizeHandler);
            }
        };
    }, [initializeGallery]);

    // Handle escape key
    React.useEffect(() => {
        const window = getWindow();
        if (!window) return;

        const handleGalleryEscapeKeyUp = (e: KeyboardEvent) => {
            const KEY_ESCAPE = "Escape";
            if (e.key === KEY_ESCAPE) {
                handleCloseGallery();
            }
        };

        window.addEventListener("keyup", handleGalleryEscapeKeyUp);

        return () => {
            window.removeEventListener("keyup", handleGalleryEscapeKeyUp);
        };
    }, [handleCloseGallery]);

    // Load Wistia player if needed
    React.useEffect(() => {
        if (hasInitialVideoTile.current) {
            loadWistiaPlayer();
        }
    }, []);

    // Cleanup gallery on unmount
    React.useEffect(() => {
        return () => {
            if (galleryFlktyRef.current) {
                safeDestroy(galleryFlktyRef.current);
            }
        };
    }, []);

    const buildGalleryTiles = (): React.JSX.Element[] => {
        return galleryTiles.map((tile, cellIndex) => {
            // Hide non-visible Flickity cells from AT and the tab order (A360 #391).
            // cellIndex matches Flickity cells (null posts filtered out above).
            const isSelected = cellIndex === selectedSlide - 1;
            const tileContentClasses = classNames({
                "ugc-carousel-gallery-tile__content": true,
                "ugc-carousel-gallery-tile__content--text-only":
                    isTextOnly(tile),
            });
            const tileDetailsClasses = classNames({
                "ugc-carousel-gallery-tile__details": true,
                "ugc-carousel-gallery-tile__details--text-only":
                    isTextOnly(tile),
            });
            const tileUsernameClasses = classNames({
                "ugc-carousel-gallery-tile__username": true,
                "ugc-carousel-gallery-tile__username--highlight":
                    tile.post.social_link,
            });
            const tileIconContainerClasses = classNames({
                "ugc-carousel-gallery-tile__icon-container": true,
                "ugc-carousel-gallery-tile__icon-container--empty":
                    tile.post.social_media === "none",
            });

            const imageVideo =
                tile.post.post_type === "video" && tile.post.video_link ? (
                    <span
                        className="ugc-carousel-gallery-tile__video al-ugc-carousel-gallery-tile--video"
                        dangerouslySetInnerHTML={{
                            __html: tile.post.video_link,
                        }}
                    ></span>
                ) : (
                    <Image
                        src={tile.post.photo}
                        className="ugc-carousel-gallery-tile__image"
                        alt={tile.post.photo_title}
                    />
                );
            const tilePhoto = isTilePhotoOrVideo(tile) ? (
                <div className="ugc-carousel-gallery-tile__image-container">
                    {imageVideo}
                </div>
            ) : null;
            const recommendations =
                tile.post.recommendations &&
                tile.post.recommendations.length > 0 ? (
                    <UGCRecommendations
                        recommendations={tile.post.recommendations}
                    />
                ) : null;

            const tileUsername = tile.post.social_link ? (
                <a
                    id={`post-${cellIndex}`}
                    className={tileUsernameClasses}
                    href={tile.post.social_link}
                    target="_blank"
                    rel="noreferrer"
                >
                    {tile.post.username}
                </a>
            ) : (
                <span className={tileUsernameClasses}>
                    {tile.post.username}
                </span>
            );

            const onTileShareClick = (e: React.MouseEvent<HTMLElement>) => {
                gotoGalleryLink(e, tile.post.social_link);
            };
            const tileShare = tile.post.social_link ? (
                <button
                    aria-label={t`Open in Instagram`}
                    title={t`Open in Instagram`}
                    className="ugc-carousel-gallery-tile__link ugc-carousel-gallery-tile__share al-ugc-gallery__share-link"
                    onClick={onTileShareClick}
                >
                    <SVGIcon
                        name="externalLink"
                        className="ugc-carousel-gallery-tile__close-icon al-ugc-gallery__close"
                        alt={t`Open in Instagram`}
                    />
                </button>
            ) : null;

            return (
                <li
                    className="ugc-carousel-gallery-tile"
                    key={cellIndex}
                    inert={!isSelected}
                >
                    <p className="ada-screenreader-only">
                        {t`To navigate, use the left and right arrows.`}
                    </p>
                    <section className={tileContentClasses}>
                        <div className={tileDetailsClasses}>
                            <div className="ugc-carousel-gallery-tile__attribution">
                                {tile.post.avatar && (
                                    <Image
                                        className="ugc-carousel-gallery-tile__avatar"
                                        src={tile.post.avatar}
                                        alt={tile.post.avatar_title}
                                    />
                                )}
                                <div className="ugc-carousel-gallery-tile__links">
                                    <button
                                        aria-label={t`Close`}
                                        title={t`Close Modal`}
                                        className="ugc-carousel-gallery-tile__link ugc-carousel-gallery-tile__close al-ugc-gallery__close"
                                        onClick={handleCloseGallery}
                                    >
                                        <SVGIcon
                                            name="xClose"
                                            className="ugc-carousel-gallery-tile__close-icon"
                                            alt={t`Close`}
                                        />
                                    </button>
                                    {tileShare}
                                </div>
                                <h3 className="ugc-carousel-gallery-tile__title">
                                    <strong>{tile.post.title}</strong>
                                </h3>
                                <span className={tileIconContainerClasses}>
                                    <SocialIcon
                                        type={tile.post.social_media}
                                        className={
                                            "ugc-carousel-gallery-tile__icon"
                                        }
                                    />
                                </span>
                                {tileUsername}
                            </div>
                            <div className="ugc-carousel-gallery-tile__copy">
                                {!isMobile && tile.post.copy}
                                {!isMobile && isMediumScreen && recommendations}
                            </div>
                            {!isMobile && !isMediumScreen && recommendations}
                        </div>
                        <div className="ugc-carousel-gallery-tile__photo-and-copy">
                            {!isMobile && tilePhoto}
                            {isMobile && (
                                <div>
                                    {tilePhoto}
                                    <div className="ugc-carousel-gallery-tile__copy-container">
                                        <div className="ugc-carousel-gallery-tile__copy">
                                            {tile.post.copy}
                                            {recommendations}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                </li>
            );
        });
    };

    const galleryAriaLabel = isGalleryFullscreen
        ? brandName
            ? t`${brandName} Social Media Posts`
            : t`Social Media Posts`
        : brandName
          ? t`${brandName} Instagram post carousel`
          : t`Instagram post carousel`;

    return (
        <div
            role={isGalleryFullscreen ? "dialog" : "region"}
            aria-modal={isGalleryFullscreen || undefined}
            aria-label={galleryAriaLabel}
            className="ugc-carousel-gallery__content"
            ref={galleryCarouselRef}
        >
            {galleryTiles.length ? (
                <p className="ada-screenreader-only">
                    {t`This is a carousel. Use the previous and next buttons to move between posts.`}
                </p>
            ) : null}
            {isMobile && (
                <span className="ugc-carousel-gallery__counter">
                    {selectedSlide} of {galleryTiles.length}
                </span>
            )}
            <ul>{buildGalleryTiles()}</ul>
        </div>
    );
};
